import { groupForYouChats, type SidepanelChat } from '@renderer/overlay/hooks/running-chat-selection'
import { toTimeframeRows } from '@renderer/overlay/Sidepanel/utils/group-by-timeframe'
import type { StagedAction } from '../../hooks/use-staged-actions'

export type ForYouListRow =
  | { kind: 'label'; label: string; isFirst: boolean }
  | { kind: 'in-progress'; chats: Array<SidepanelChat> }
  | { kind: 'chat'; chat: SidepanelChat; position: number }
  | { kind: 'view-more' }
  | { kind: 'action'; action: StagedAction }

type ForYouDatedItem =
  | { kind: 'chat'; chat: SidepanelChat; position: number }
  | { kind: 'action'; action: StagedAction }

function itemDate(item: ForYouDatedItem): Date {
  return item.kind === 'chat' ? new Date(item.chat.updatedAt) : new Date(item.action.updatedAt)
}

export function toForYouListRows(
  chats: Array<SidepanelChat>,
  actions: Array<StagedAction>,
  hasNextPage: boolean,
  now: Date = new Date(),
): Array<ForYouListRow> {
  const { previewlessStreaming, visibleStreaming, actionable } = groupForYouChats(chats)
  const positions = new Map(chats.map((chat, index) => [chat.id, index]))
  const rows: Array<ForYouListRow> = []

  if (previewlessStreaming.length > 0) {
    rows.push({ kind: 'in-progress', chats: previewlessStreaming })
  }

  const dated: Array<ForYouDatedItem> = [
    ...visibleStreaming.map((chat): ForYouDatedItem => ({ kind: 'chat', chat, position: positions.get(chat.id) ?? 0 })),
    ...actionable.map((chat): ForYouDatedItem => ({ kind: 'chat', chat, position: positions.get(chat.id) ?? 0 })),
    ...actions.map((action): ForYouDatedItem => ({ kind: 'action', action })),
  ].sort((a, b) => itemDate(b).getTime() - itemDate(a).getTime())

  const hasLeading = rows.length > 0
  for (const row of toTimeframeRows(dated, itemDate, now)) {
    if (row.kind === 'label') {
      rows.push({ kind: 'label', label: row.label, isFirst: row.isFirst && !hasLeading })
      continue
    }
    rows.push(row.item)
  }

  if (hasNextPage) {
    rows.push({ kind: 'view-more' })
  }

  return rows
}

export function forYouListRowKey(row: ForYouListRow): string {
  switch (row.kind) {
    case 'label': {
      return `label:${row.label}`
    }
    case 'in-progress': {
      return 'in-progress'
    }
    case 'chat': {
      return `chat:${row.chat.id}`
    }
    case 'view-more': {
      return 'view-more'
    }
    case 'action': {
      return `action:${row.action.id}`
    }
  }
}
